import { useCallback, useEffect, useId, useRef, useState } from 'react'

import type { Load } from '../api/useLoad'
import type { ActiveSpace } from '../api/space'
import { signOut, type Session } from '../api/session'
import { asFailure, type Failure } from '../api/request'
import { Button, LinkButton } from '../ui/Control'
import { Failed, Waiting } from '../ui/Status'
import { Icon } from '../ui/Icon'
import { useAnnounce } from '../ui/Announce'
import { hrefFor } from './route'

/**
 * Who you are and which space you are in — visible in the frame at all times, not tucked away.
 *
 * The space owns everything in MyLib, so which one is active is never a detail. There is exactly
 * one per person today; the moment there are more, this is where it is read off and this is where
 * it would be switched.
 *
 * The chip carries the two names. What is behind it is the rest — the space's own identifier, a
 * failure to fetch it, and the way out.
 */
export interface AccountProps {
  readonly session: Session
  readonly space: Load<ActiveSpace>
  readonly onRetrySpace: () => void
  readonly onSignedOut: () => void
}

/** Until a space carries a name of its own, this is what one is called. It is not a guess. */
const UNNAMED_SPACE = 'Your space'

export function Account({ session, space, onRetrySpace, onSignedOut }: AccountProps) {
  const [open, setOpen] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const [problem, setProblem] = useState<Failure | null>(null)
  const announce = useAnnounce()
  const panelId = useId()
  const chip = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)

  const close = useCallback(() => {
    setOpen(false)
    chip.current?.focus()
  }, [])

  useEffect(() => {
    if (!open) {
      return
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close()
      }
    }
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node)) {
        return
      }
      if (panel.current?.contains(target) === true || chip.current?.contains(target) === true) {
        return
      }
      setOpen(false)
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open, close])

  const leave = useCallback(() => {
    setSigningOut(true)
    setProblem(null)
    announce('Signing out.')
    signOut(session)
      .then(onSignedOut)
      .catch((error: unknown) => {
        setSigningOut(false)
        setProblem(asFailure(error))
      })
  }, [session, onSignedOut, announce])

  const spaceName = space.status === 'ready' ? (space.value.name ?? UNNAMED_SPACE) : UNNAMED_SPACE
  const userName = session.userName ?? 'you'

  return (
    <div className="account">
      <Button
        ref={chip}
        tone="bare"
        className="account__chip"
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="dialog"
        /* Named rather than read off its contents, so it says the same thing on a narrow screen
           where the two names do not fit and only the mark is shown. */
        aria-label={`Account: ${userName} in ${spaceName}`}
        onClick={() => {
          setOpen((was) => !was)
        }}
      >
        <span className="account__mark" aria-hidden="true">
          {initialOf(session.userName)}
        </span>
        <span className="account__names">
          <span className="account__space">{spaceName}</span>
          <span className="account__user">{userName}</span>
        </span>
        {space.status === 'failed' && <Icon name="warning" small />}
      </Button>

      {open && (
        <div className="account__panel" id={panelId} ref={panel} role="dialog" aria-label="Account">
          <h2 className="account__panel-title">Account</h2>

          <dl className="facts">
            <div className="facts__row">
              <dt className="facts__key">Signed in as</dt>
              <dd className="facts__value">{userName}</dd>
            </div>
            <div className="facts__row">
              <dt className="facts__key">Space</dt>
              <dd className="facts__value">
                {space.status === 'waiting' && <Waiting label="Looking up your space" />}
                {space.status === 'ready' && (space.value.name ?? UNNAMED_SPACE)}
                {space.status === 'failed' && 'Not known just now'}
              </dd>
            </div>
            {space.status === 'ready' && (
              <div className="facts__row">
                <dt className="facts__key">Identifier</dt>
                <dd className="facts__value">{space.value.id}</dd>
              </div>
            )}
          </dl>

          {space.status === 'failed' && <Failed failure={space.failure} onRetry={onRetrySpace} />}

          {problem !== null && <Failed failure={problem} title="Signing out did not work" />}

          <div className="controls">
            {/*
              The way to the systems MyLib is allowed to sign in to on your behalf.

              It is here because that is what it is about: credentials of yours, kept by MyLib, doing
              something in your name — which is the same subject as who you are and which space you
              are in. It is also something set up once a semester, so it earns a line behind this
              chip rather than a symbol in the navigation.

              A link and not a button, so it can be opened in a tab of its own and so its address is
              visible before it is followed. The panel stands over the area it opens, and therefore
              closes as it is followed.
            */}
            <LinkButton
              tone="quiet"
              href={hrefFor('connections')}
              onClick={() => {
                setOpen(false)
              }}
            >
              External systems
            </LinkButton>
            {/*
              The same subject seen from the other side: there MyLib is given a way into somebody
              else's system, here somebody else's assistant is given a way into MyLib. Both are
              credentials, both are set up once, and both belong behind this chip rather than in the
              navigation.
            */}
            <LinkButton
              tone="quiet"
              href={hrefFor('assistants')}
              onClick={() => {
                setOpen(false)
              }}
            >
              Assistants
            </LinkButton>
            <Button tone="quiet" onClick={leave} disabled={signingOut}>
              {signingOut ? 'Signing out…' : 'Sign out'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function initialOf(name: string | null): string {
  const first = name?.trim().charAt(0)
  return first === undefined || first === '' ? '?' : first
}
